import type { Object as ParseObject, User, Attributes } from 'parse';
import { readAcq } from 'src/utils/acquisition'
import { Platform } from 'quasar';
import { Parse } from 'src/boot/parse'
import { UserData } from 'src/types';
import { FLAVOR_SETTING } from 'src/flavors/settings';
import { flavorLockBlock, FlavorLockError, isGeneralBuild } from 'src/utils/flavorLock';
import { reactive, ref, Ref } from 'vue'

/**
 * Builds the `registered_using` tag: `planner_web[_ios|_and][_<flavor>][_<suffix>]`.
 * Shared by admin signup (register) and coworker creation (createCoworkerUser) so
 * BOTH carry the flavor — the server's emails.js matches on includes('planner') then
 * includes(<flavor>) to pick the follow-up template. The flavor keyword comes from
 * FLAVOR_SETTING.userRegistered_using (override) else BUSINESS_TYPE; 'table' adds none.
 * `suffix` tags the account kind, e.g. 'coworker' → 'planner_web_car_coworker'.
 */
export function registeredUsingTag(suffix?: string): string {
  let tag = 'planner_web'
  if (Platform.is.capacitor) {
    if (Platform.is.ios) tag += '_ios'
    else if (Platform.is.android) tag += '_and'
  }
  const businessType = process.env.BUSINESS_TYPE || 'table'
  const flavor = (FLAVOR_SETTING as Record<string, { userRegistered_using?: string }>)[businessType]?.userRegistered_using
    ?? process.env.BUSINESS_TYPE
  if (flavor && flavor !== 'table') tag += `_${flavor}`
  if (suffix) tag += `_${suffix}`
  return tag
}

const defaultUser: UserData = {
  name: '',
  id: '',
  username: '',
  country: '',
  email: '',
  language: 1,
  image: '',
  admin: null,
  phone: '',
  // ⚠️ MUST be listed here or getUserProps() never copies it into `userProps`, and the
  // demo lock in utils/testAccount.ts reads undefined forever — silently unlocked.
  demo_locked: undefined
}

const user = ref<ReturnType<typeof Parse.User.current> | null>(null)
try {
  user.value = Parse.User.current()
} catch {
  // Parse not yet initialized (module loaded before boot); refreshed in initUser()
}
const userProps = reactive(structuredClone(defaultUser))
if(user.value) {
  getUserProps()
}

export function initUser() {
  try {
    user.value = Parse.User.current()
    if (user.value) getUserProps()
  } catch { /* */ }
}

/**
 * Re-read the signed-in user FROM THE SERVER.
 *
 * `Parse.User.current()` is served from localStorage, so a field changed elsewhere —
 * `demo_locked` ticked in the Parse dashboard, say — would otherwise not be seen until
 * the next login. Called at app start and when the app returns to the foreground.
 *
 * Best-effort by design: offline or a server hiccup must not break start-up — the cached
 * copy is kept.
 *
 * ⚠️ EXCEPT an INVALID SESSION (Parse 209). Keeping the cached copy then means running
 * the whole app on a dead token: every request fails, and the router's flavor guard read
 * its own failure as "this business belongs to another flavor" (found 2026-09-29). A
 * session dies when any tab or device logs out with the same token — e.g. dev:spa and
 * dev:wellza share localhost:9003 and therefore one session. So: clear it locally and
 * return 'expired'; the caller sends the user to the login page.
 */
export async function refreshUser(): Promise<'ok' | 'expired' | 'skipped'> {
  const current = Parse.User.current()
  if (!current) return 'skipped'
  try {
    await current.fetch()
    user.value = current
    getUserProps()
    return 'ok'
  } catch (err) {
    if ((err as { code?: number })?.code === Parse.Error.INVALID_SESSION_TOKEN) {
      await clearDeadSession()
      return 'expired'
    }
    console.warn('[useAuth] could not refresh the user:', err)
    return 'skipped'
  }
}

/**
 * Forget a session the server no longer knows. Parse.User.logOut() clears local storage
 * even though its server call fails for a dead token — that failure is expected, swallowed.
 */
export async function clearDeadSession() {
  try { await Parse.User.logOut() } catch { /* the server already forgot it */ }
  user.value = null
  clearUserProps()
}

// const verificationCode = ref('')

function getUserProps(payload: User<Attributes> | undefined = undefined) {
  if(!user.value) {
    user.value = payload
  }
  for (const prop in defaultUser) {
    if (prop == 'image') {
      const image = user.value?.get(prop)
      userProps.image = image?.url()
      continue
    }
    if (prop == 'id') {
      // @ts-ignore
      userProps.id = user.value?.id
      continue
    }
    // @ts-ignore
    userProps[prop] = user.value?.get(prop)
  }
}

function clearUserProps() {
  for (const prop in defaultUser) {
    // @ts-ignore
    userProps[prop] = defaultUser[prop]
  }
}

export default function useAuth() {

  async function login(username: string, password: string) {

    try {
      await Parse.User.logIn(username.toLowerCase().trim(), password.trim())
    } catch(err) {
      console.warn(err)
      return false
    }

    // Flavor isolation (general DB only): this user must belong to THIS flavor —
    // else log them straight back out and surface a flavor-locked error to the UI.
    const current = Parse.User.current()
    const block = flavorLockBlock(current?.get('registered_using'))
    if (block) {
      await Parse.User.logOut()
      throw new FlavorLockError(block.otherFlavor)
    }

    user.value = current
    getUserProps(user.value)
    return true
  }

  async function logout() {
    await Parse.User.logOut()
    user.value = Parse.User.current()
    if(!user.value) {
      clearUserProps()
    }
  }

  async function register(userData: { email: string; password: string; name: string; phone: string; country: string; language?: number; }) {
    return new Promise(async (resolve, reject) => {
      userData.email = userData.email.toLowerCase()

      // Flavor isolation (general DB only): block registering an email that already
      // belongs to ANOTHER general flavor (or is untagged). Same-flavor duplicates fall
      // through to signUp's normal "email already taken" (→ log in instead).
      if (isGeneralBuild) {
        const existing = await new Parse.Query(Parse.User)
          .equalTo('username', userData.email)
          .first()
        const block = existing && flavorLockBlock(existing.get('registered_using'))
        if (block) { reject(new FlavorLockError(block.otherFlavor)); return }
      }

      // Tag with platform + flavor so the server sends the right welcome email
      // (planner_web[_ios|_and][_<flavor>]). See registeredUsingTag() above.
      const platform = registeredUsingTag()

      // WHERE they came from, captured silently when they first landed — see
      // src/utils/acquisition.ts. `registered_using` says which DOOR they came through;
      // this says which road led to it. Null for a direct visit and for every native
      // install, and that absence is itself the answer.
      const acq = readAcq()

      // WHERE they are: the phone's time zone ('Europe/Madrid'). Sign-up no longer asks for a
      // country (2026-10-01) — it is picked on the business — so for an owner who leaves
      // before creating one this is the only hint, and the daily report turns it into a flag.
      // ⚠️ The server must have the `signup_tz` column first (cloud/hooks/userSchema.js),
      // or this sign-up fails with "addField on class _User".
      let signupTz = ''
      try { signupTz = Intl.DateTimeFormat().resolvedOptions().timeZone || '' } catch { /* old engine */ }

      try {
        await new Parse.User({
          // Preferred-language index — server's numeral mapping (getLocaleFromIndex);
          // drives the welcome email + notifications. 0 = en fallback.
          language: userData.language ?? 0,
          blocked: false,
          is_admin: true,
          registered_using: platform,
          // Spread, so a direct visit adds no key at all rather than a null column.
          ...(acq ? { acq } : {}),
          ...(signupTz ? { signup_tz: signupTz } : {}),
          email: userData.email,
          username: userData.email,
          password: userData.password,
          name: userData.name,
          phone: userData.phone.replace(/\D/g, ''),
          country: userData.country
        }).signUp()

        const isLogin = await login(userData.email, userData.password)

        if(isLogin) resolve(true)
        else resolve(false)
      } catch (e: any) {
        // Duplicate email is NOT a failure to apologise for: every app in the family
        // (Planner / DreamDiner Admin / Shiftaurant) signs into the SAME `_User` class,
        // so an email can be registered only once across all of them. They already have
        // an account and simply need to sign in. Rejected as a coded error so the form
        // can say that, instead of printing Parse's raw "Account already exists for this
        // username" after a generic "Something went wrong".
        if (e?.code === Parse.Error.USERNAME_TAKEN || e?.code === Parse.Error.EMAIL_TAKEN) {
          const exists: any = new Error('USER_EXISTS')
          exists.code = 'USER_EXISTS'
          reject(exists)
          return
        }
        reject(e.message)
      }
    })
  }

  function verifyOtp(otp: string): Promise<{ success: boolean, error: string }> {
    return new Promise(resolve => {
      new Parse.Query('User')
        .equalTo('objectId', Parse.User.current()?.id)
        .equalTo('verification_code', parseInt(otp))
        .count()
        .then(count => {
          const user = Parse.User.current()
          if (count > 0 && !!user) {
              // @ts-ignore
            user.set({ emailVerified2: true }).save()
              .then(() => {
                resolve({ success: true, error: '' })
              })
          } else {
            resolve({
              success: false,
              error: 'Incorrect code'
            })
          }
        }).catch(error => {
          console.log('#### auth verifyOtp error', error)
          resolve({
            success: false,
            error
          })
        })
    })
  }

  function resendVerificationEmail() {
    return new Promise(async resolve => {
      Parse.Cloud.run('resendEmailVerification').then(() => {
        resolve({
          success: true
        })
      }).catch(error => {
        console.log('#### auth resendVerificationEmail error', error)
        resolve({
          success: false,
          error
        })
      })
    })
  }

  async function sendPasswordResetLink(email: string) {
    const normalizedEmail = email.toLowerCase().trim();
    return Parse.User.requestPasswordReset(normalizedEmail);
  }

  // --- Coworker phone login ---
  // ⚠️ DEPRECATED, and no longer called by anything. `sendCoworkerOtp` + `loginByPhone`
  // are the OLD pair: the server hands the OTP to this browser, we compare it here, then
  // sign in with `password === username === the phone digits`. That made the code a UI
  // gate and nothing more. Kept only as a revert path while the new flow rolls out to the
  // app stores; delete both once Phase 2 lands. Use sendCoworkerLoginCode /
  // loginCoworkerWithCode below.
  //
  // Calls the Parse Cloud 'sendVerificationCode' function (same as Admin).
  // Returns { code: '3-digit OTP', name: 'coworker name' }
  // Throws { code: 'NOT_ASSIGNED' } if the phone number has no _User or no staff assignments.
  async function sendCoworkerOtp(dialCode: string, phone: string): Promise<{ code: string; name: string }> {
    const cleanPhone = phone.replace(/\D/g, '')
    // 1. Find the _User by phone
    const users = await new Parse.Query('_User')
      .equalTo('phone', cleanPhone)
      .find()
    const coworkerUser = users[0] ?? null

    if (!coworkerUser) {
      throw Object.assign(new Error('NOT_ASSIGNED'), { code: 'NOT_ASSIGNED' })
    }

    // Flavor isolation (general DB only): a coworker locked to another flavor can't
    // sign in here — blocked before any OTP is sent.
    const flavorBlock = flavorLockBlock(coworkerUser.get('registered_using'))
    if (flavorBlock) throw new FlavorLockError(flavorBlock.otherFlavor)

    // 2. Verify they are assigned to at least one business. Restaurant flavors use the
    // 3-role classes (Waiter/Kitchen/SubAdmin); single-coworker flavors (car/room/…) use
    // their per-flavor coworker class. Querying the wrong ones 400s on the general backend
    // where those classes don't even exist.
    const businessType = process.env.BUSINESS_TYPE || 'table'
    const coworkerClass = (FLAVOR_SETTING as Record<string, { coworkerClass?: string }>)[businessType]?.coworkerClass
    const roleClasses = coworkerClass ? [coworkerClass] : ['Waiter', 'Kitchen', 'SubAdmin']
    const counts = await Promise.all(
      roleClasses.map((cls) => new Parse.Query(cls).equalTo('user', coworkerUser).count())
    )
    if (counts.reduce((sum, n) => sum + n, 0) === 0) {
      throw Object.assign(new Error('NOT_ASSIGNED'), { code: 'NOT_ASSIGNED' })
    }

    // 3. Send OTP
    const otpCode = await Parse.Cloud.run('sendVerificationCode', {
      from: 'DreamDiner',
      phoneNumber: dialCode + phone.replace(/^0+/, ''),
    })
    return { code: String(otpCode), name: coworkerUser.get('name') || '' }
  }

  /**
   * Ask the SERVER to send a coworker a login OTP. Returns the coworker's name; it does
   * NOT return the code, and there is no way to ask it for one.
   *
   * ⚠️ Replaces `sendCoworkerOtp` for the login screen. That one calls the shared
   * `sendVerificationCode`, which HANDS THE CODE BACK to the browser — fine when the code
   * only unlocks a UI step, useless as proof of anything, because whoever calls it can
   * read the answer out of the response. Pair this with `loginCoworkerWithCode`, which
   * checks the code server-side and issues the session itself.
   *
   * Throws FlavorLockError so the caller can reuse the existing flavor-lock messaging;
   * every other outcome comes back as `{ ok: false, reason }`.
   */
  async function sendCoworkerLoginCode(
    dialCode: string,
    phone: string,
    language?: number | null,
  ): Promise<{ ok: boolean; name?: string; reason?: string }> {
    const res = await Parse.Cloud.run('sendCoworkerLoginCode', {
      dialCode,
      phone,
      language: typeof language === 'number' ? language : undefined,
      registered_using: registeredUsingTag('coworker'),
    })
    if (!res?.ok && res?.reason === 'flavor_locked') {
      throw new FlavorLockError(res.otherFlavor ?? null)
    }
    return res
  }

  /**
   * Redeem the OTP for a session. The server checks the code, re-checks that this person
   * is still staff somewhere, and mints the session with the master key; the browser
   * adopts it with `become()`.
   *
   * ⚠️ No password is involved anywhere in this path — which is the point. See
   * cloud/team/coworkerLogin.js for why the OTP alone was not enough before.
   */
  async function loginCoworkerWithCode(
    dialCode: string,
    phone: string,
    code: string,
    language?: number | null,
  ): Promise<{ ok: boolean; reason?: string; attemptsLeft?: number }> {
    const res = await Parse.Cloud.run('loginCoworkerWithCode', {
      dialCode,
      phone,
      code,
      language: typeof language === 'number' ? language : undefined,
    })
    if (!res?.ok) return res

    // `become` swaps the SDK's current user to the session the server just issued.
    await Parse.User.become(res.sessionToken)
    user.value = Parse.User.current()
    getUserProps(user.value)
    return { ok: true }
  }

  /**
   * Sign in with Google / Apple. The server verifies the provider token, finds the account
   * by the VERIFIED email (one account per email) and mints the session; the browser adopts
   * it with `become()` — the same shape as loginCoworkerWithCode above.
   *
   * mode 'login' never creates an account: an unknown email comes back as
   * { ok:false, reason:'no_account', email } and the screen asks before creating one.
   */
  async function loginWithSocial(
    provider: 'google' | 'apple',
    idToken: string,
    mode: 'login' | 'signup',
    language?: number | null,
    name?: string,
    audience: 'owner' | 'coworker' = 'owner',
  ): Promise<{ ok: boolean; isNew?: boolean; reason?: string; email?: string; flavor?: string | null }> {
    const res = await Parse.Cloud.run('socialLogin', {
      provider,
      idToken,
      mode,
      registeredUsing: registeredUsingTag(),
      language: typeof language === 'number' ? language : undefined,
      name: name || undefined,
      audience,
    })
    if (!res?.ok) return res || { ok: false, reason: 'failed' }

    await Parse.User.become(res.sessionToken)
    // Same flavor isolation as login() (only bites on the general DB; DreamDiner is never gated).
    const current = Parse.User.current()
    const block = flavorLockBlock(current?.get('registered_using'))
    if (block) {
      await Parse.User.logOut()
      throw new FlavorLockError(block.otherFlavor)
    }
    user.value = current
    getUserProps(user.value)
    return { ok: true, isNew: !!res.isNew }
  }

  // Logs in a coworker using dialCode+phone as both username and password.
  // The _User must already exist (created via signUpCoworker Cloud fn on save).
  /**
   * @param language  server language index to stamp on the coworker.
   *
   * Coworkers are created by the ADMIN who invites them, and that path never captured a
   * language — so every coworker landed in the DB with `language` undefined, and the
   * server then had nothing to pick a locale from for their emails and WhatsApp. The
   * login screen is the first moment the coworker themselves is present to say, so it is
   * saved here rather than left to a profile visit that may never happen.
   *
   * Best-effort: a failed save must not block a valid login. Skipped when unchanged so a
   * returning coworker does not write on every sign-in.
   */
  async function loginByPhone(dialCode: string, phone: string, language?: number | null): Promise<boolean> {
    try {
      // dialCode may come with or without '+' prefix — normalize to no prefix (e.g. "49")
      const code = dialCode.replace(/^\+/, '')
      const username = code + phone.replace(/^0+/, '')
      await Parse.User.logIn(username, username)
      user.value = Parse.User.current()
      if (typeof language === 'number' && user.value && user.value.get('language') !== language) {
        try { await user.value.save({ language }) }
        catch (e) { console.warn('[loginByPhone] could not save language', e) }
      }
      getUserProps(user.value)
      return true
    } catch (err) {
      console.warn('[loginByPhone] error', err)
      return false
    }
  }

  return {
    user,
    userProps,
    login,
    logout,
    register,
    getUserProps,
    verifyOtp,
    resendVerificationEmail,
    sendPasswordResetLink,
    sendCoworkerOtp,
    loginByPhone,
    // The password-free coworker login. Prefer these two over sendCoworkerOtp/loginByPhone.
    sendCoworkerLoginCode,
    loginCoworkerWithCode,
    loginWithSocial,
    ParseUser: Parse.User.current()
  }
}
